import {
  useContext,
  useEffect,
  useRef,
  useState,
  Children,
  isValidElement
} from "react";
const useKeyboardEvents = ({
  initRef,
  listRef,
  searchInputRef,
  isAutocomplete = false,
  context,
  children,
  childrenRef,
  multiselectIndex = { current: -1 },
  multiselectHoveredOption
}) => {
  const searchString = useRef("");
  const timeout = useRef();
  const [currentOptions, setCurrentOptions] = useState([]);
  const {
    menuId,
    isExpanded,
    isMultiselect,
    setIsExpanded,
    selectedValue,
    onChange,
    setSelectedValue,
    onSelection,
    filteredOptions
  } = useContext(context);
  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);
  const createFlatChildArray = (c) => Children.toArray(c).map((el) => {
    if (isValidElement(el)) {
      return el?.props?.value;
    }
    return el;
  });
  useEffect(() => {
    setCurrentOptions(createFlatChildArray(children));
  }, [children, selectedValue]);
  return (event) => {
    const { key } = event;
    const listElement = listRef.current;
    const buttonElement = initRef.current;
    const newEvent = { ...event };
    const currentlySelectedIndex = isMultiselect ? multiselectIndex.current : currentOptions.indexOf(selectedValue);
    const listIsActive = document.activeElement === listElement;
    const buttonIsActive = document.activeElement === buttonElement;
    if (!listElement || !currentOptions.length)
      return;
    const focusList = () => {
      if (!listIsActive) {
        listElement.focus();
      }
    };
    const changeWithValue = (newValue) => {
      newEvent.target.value = newValue;
      if (onChange) {
        if (isMultiselect)
          return;
        onChange(newEvent);
      }
      if (setSelectedValue)
        setSelectedValue(newValue);
    };
    const getArrowDownIndex = (n) => n < 0 ? 0 : n + 1;
    const scrollArrowKeyOption = (prevChild, nextChild) => {
      const activeKeyClass = "is-key-active";
      prevChild?.classList?.remove(activeKeyClass);
      nextChild?.classList?.add(activeKeyClass);
      listElement.scrollTo({
        top: nextChild?.offsetTop,
        behavior: "smooth"
      });
    };
    const selectOptionAndCloseDropdown = () => {
      const currentChild = multiselectChildren[multiselectIndex.current];
      currentChild?.click();
      setIsExpanded(false);
      buttonElement.focus();
    };
    let multiselectChildren = [];
    if (isMultiselect) {
      const filteredOptionsWithId = filteredOptions?.map((v) => `${menuId}_${v}`);
      multiselectChildren = childrenRef?.current?.filter((data) => data && filteredOptionsWithId?.includes(data?.id)) ?? [];
    }
    if (document.activeElement === searchInputRef?.current)
      return;
    switch (key) {
      case " ": {
        if (isMultiselect) {
          selectOptionAndCloseDropdown();
        }
        break;
      }
      case "ArrowDown": {
        if (!isExpanded) {
          setIsExpanded(true);
        }
        focusList();
        if (currentlySelectedIndex === currentOptions.length - 1)
          return;
        if (isMultiselect && multiselectIndex.current === multiselectChildren.length - 1)
          return;
        const nextIndex = getArrowDownIndex(isMultiselect ? multiselectIndex.current : currentlySelectedIndex);
        if (isMultiselect) {
          multiselectIndex.current = nextIndex;
          const prevChild = multiselectChildren[nextIndex > 0 ? nextIndex - 1 : 0];
          const nextChild = multiselectChildren[nextIndex];
          if (multiselectHoveredOption) {
            multiselectHoveredOption.current = nextChild;
          }
          scrollArrowKeyOption(prevChild, nextChild);
        }
        changeWithValue(currentOptions[nextIndex]);
        if (onChange) {
          if (isMultiselect)
            return;
          onChange(newEvent);
        }
        if (setSelectedValue)
          setSelectedValue(event?.target?.value);
        break;
      }
      case "ArrowUp": {
        if (!isExpanded) {
          setIsExpanded(true);
        }
        focusList();
        if (currentlySelectedIndex <= 0)
          return;
        const nextIndex = isMultiselect ? multiselectIndex.current - 1 : currentlySelectedIndex - 1;
        if (isMultiselect) {
          multiselectIndex.current = nextIndex;
          const prevChild = multiselectChildren[nextIndex + 1];
          const nextChild = multiselectChildren[nextIndex];
          if (multiselectHoveredOption) {
            multiselectHoveredOption.current = nextChild;
          }
          scrollArrowKeyOption(prevChild, nextChild);
        }
        changeWithValue(currentOptions[nextIndex]);
        break;
      }
      case "Enter": {
        if (isMultiselect && listIsActive) {
          selectOptionAndCloseDropdown();
          return;
        }
        if (listIsActive) {
          setIsExpanded(false);
          buttonElement.focus();
          if (onSelection)
            onSelection(currentOptions[currentlySelectedIndex]);
        }
        if (buttonIsActive && isExpanded) {
          listElement.focus();
        }
        break;
      }
      case "Home": {
        const [firstValue] = currentOptions;
        if (isExpanded && selectedValue !== firstValue) {
          focusList();
          changeWithValue(firstValue);
        }
        break;
      }
      case "End": {
        const lastValue = currentOptions[currentOptions.length - 1];
        if (isExpanded && selectedValue !== lastValue) {
          focusList();
          changeWithValue(lastValue);
        }
        break;
      }
      case "Escape": {
        if (isExpanded) {
          setIsExpanded(false);
          buttonElement.focus();
        }
        break;
      }
      default: {
        if (isAutocomplete)
          return;
        searchString.current += key;
        clearTimeout(timeout.current);
        if (typeof window === "undefined")
          return;
        timeout.current = window.setTimeout(() => {
          const selectedIndex = currentOptions.indexOf(selectedValue);
          const sortedOptions = [
            ...currentOptions.slice(selectedIndex + 1),
            ...currentOptions.slice(0, selectedIndex + 1)
          ];
          const searchArray = selectedIndex < 0 ? currentOptions : sortedOptions;
          const match = searchArray.find((x) => x.toLowerCase().startsWith(searchString.current.toLowerCase()));
          if (match) {
            if (isMultiselect) {
              const childMatch = multiselectChildren.find((el) => el.id === `${menuId}_${match}`);
              if (childMatch) {
                listElement.scrollTo({
                  top: childMatch?.offsetTop,
                  behavior: "smooth"
                });
                childMatch.click();
              }
            }
            changeWithValue(match);
          }
          searchString.current = "";
        }, 200);
      }
    }
  };
};
export default useKeyboardEvents;
